import React, { useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProducts } from '../hooks/useProducts';
import { getCategoryById } from '../services/categoryService';
import { KashrutBadge } from '../components/KashrutBadge';
import { DepartmentBadge } from '../components/DepartmentBadge';
import { ProductFormModal } from '../components/ProductFormModal';
import { DeleteProductModal } from '../components/DeleteProductModal';
import type { ProductDto } from '../types/product';
import { productImageUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import '../styles/ProductList.css';

// ── Category theme map ──────────────────────────────────
interface CatTheme {
  bg:        string;   // page background
  tableBg:   string;   // table rows background
  thBg:      string;   // thead background
  thBorder:  string;   // thead bottom border
  rowHover:  string;   // tbody row hover
  wrapBorder:string;   // table wrapper border
  mfgHeader: string;   // manufacturer group header bg
}

const CATEGORY_THEMES: Record<number, CatTheme> = {
  1:  { bg:'linear-gradient(160deg,#f0fdf4,#dcfce7,#f0fdf4)',  tableBg:'#f0fdf4', thBg:'#bbf7d0', thBorder:'#86efac', rowHover:'#dcfce7', wrapBorder:'#86efac', mfgHeader:'#dcfce7' },
  2:  { bg:'linear-gradient(160deg,#fffbeb,#fef3c7,#fff7ed)',  tableBg:'#fffbeb', thBg:'#fde68a', thBorder:'#fbbf24', rowHover:'#fef3c7', wrapBorder:'#fbbf24', mfgHeader:'#fef3c7' },
  3:  { bg:'linear-gradient(160deg,#eff6ff,#dbeafe,#eff6ff)',  tableBg:'#eff6ff', thBg:'#bfdbfe', thBorder:'#93c5fd', rowHover:'#dbeafe', wrapBorder:'#93c5fd', mfgHeader:'#dbeafe' },
  4:  { bg:'linear-gradient(160deg,#fefce8,#fef9c3,#fffbeb)',  tableBg:'#fefce8', thBg:'#fef08a', thBorder:'#facc15', rowHover:'#fef9c3', wrapBorder:'#facc15', mfgHeader:'#fef9c3' },
  5:  { bg:'linear-gradient(160deg,#fefce8,#fef08a,#fefce8)',  tableBg:'#fefce8', thBg:'#fde047', thBorder:'#eab308', rowHover:'#fef9c3', wrapBorder:'#eab308', mfgHeader:'#fef9c3' },
  6:  { bg:'linear-gradient(160deg,#fff7ed,#ffedd5,#fef3c7)',  tableBg:'#fff7ed', thBg:'#fed7aa', thBorder:'#fb923c', rowHover:'#ffedd5', wrapBorder:'#fb923c', mfgHeader:'#ffedd5' },
  7:  { bg:'linear-gradient(160deg,#fff7ed,#fed7aa,#fef3c7)',  tableBg:'#fff7ed', thBg:'#fdba74', thBorder:'#f97316', rowHover:'#fed7aa', wrapBorder:'#f97316', mfgHeader:'#fed7aa' },
  8:  { bg:'linear-gradient(160deg,#fefce8,#fef9c3,#fff7ed)',  tableBg:'#fefce8', thBg:'#fef08a', thBorder:'#facc15', rowHover:'#fef9c3', wrapBorder:'#facc15', mfgHeader:'#fef9c3' },
  9:  { bg:'linear-gradient(160deg,#faf5ff,#ede9fe,#faf5ff)',  tableBg:'#faf5ff', thBg:'#ddd6fe', thBorder:'#a78bfa', rowHover:'#ede9fe', wrapBorder:'#a78bfa', mfgHeader:'#ede9fe' },
  10: { bg:'linear-gradient(160deg,#fff1f2,#ffe4e6,#fff7ed)',  tableBg:'#fff1f2', thBg:'#fecdd3', thBorder:'#fb7185', rowHover:'#ffe4e6', wrapBorder:'#fb7185', mfgHeader:'#ffe4e6' },
  11: { bg:'linear-gradient(160deg,#f7fee7,#ecfccb,#f0fdf4)',  tableBg:'#f7fee7', thBg:'#d9f99d', thBorder:'#84cc16', rowHover:'#ecfccb', wrapBorder:'#84cc16', mfgHeader:'#ecfccb' },
  12: { bg:'linear-gradient(160deg,#fff7ed,#ffedd5,#fef9c3)',  tableBg:'#fff7ed', thBg:'#fed7aa', thBorder:'#fb923c', rowHover:'#ffedd5', wrapBorder:'#fb923c', mfgHeader:'#ffedd5' },
  13: { bg:'linear-gradient(160deg,#fff1f2,#fecdd3,#fff1f2)',  tableBg:'#fff1f2', thBg:'#fda4af', thBorder:'#f43f5e', rowHover:'#fecdd3', wrapBorder:'#f43f5e', mfgHeader:'#fecdd3' },
  16: { bg:'linear-gradient(160deg,#fafaf9,#f5f5f4,#fef9c3)',  tableBg:'#fafaf9', thBg:'#e7e5e4', thBorder:'#a8a29e', rowHover:'#f5f5f4', wrapBorder:'#a8a29e', mfgHeader:'#f5f5f4' },
  17: { bg:'linear-gradient(160deg,#f5f3ff,#ede9fe,#f5f3ff)',  tableBg:'#f5f3ff', thBg:'#ddd6fe', thBorder:'#8b5cf6', rowHover:'#ede9fe', wrapBorder:'#8b5cf6', mfgHeader:'#ede9fe' },
  18: { bg:'linear-gradient(160deg,#fff7ed,#fed7aa,#fffbeb)',  tableBg:'#fff7ed', thBg:'#fdba74', thBorder:'#f97316', rowHover:'#fed7aa', wrapBorder:'#f97316', mfgHeader:'#fed7aa' },
  19: { bg:'linear-gradient(160deg,#f0fdf4,#dcfce7,#fff1f2)',  tableBg:'#f0fdf4', thBg:'#bbf7d0', thBorder:'#4ade80', rowHover:'#dcfce7', wrapBorder:'#4ade80', mfgHeader:'#dcfce7' },
  20: { bg:'linear-gradient(160deg,#f0fdf4,#bbf7d0,#f0fdf4)',  tableBg:'#f0fdf4', thBg:'#86efac', thBorder:'#22c55e', rowHover:'#bbf7d0', wrapBorder:'#22c55e', mfgHeader:'#bbf7d0' },
  21: { bg:'linear-gradient(160deg,#f7fee7,#d9f99d,#ecfccb)',  tableBg:'#f7fee7', thBg:'#bef264', thBorder:'#65a30d', rowHover:'#d9f99d', wrapBorder:'#65a30d', mfgHeader:'#d9f99d' },
  22: { bg:'linear-gradient(160deg,#f0fdf4,#dcfce7,#ecfccb)',  tableBg:'#f0fdf4', thBg:'#bbf7d0', thBorder:'#16a34a', rowHover:'#dcfce7', wrapBorder:'#16a34a', mfgHeader:'#dcfce7' },
  23: { bg:'linear-gradient(160deg,#fff7ed,#fdba74,#fef3c7)',  tableBg:'#fff7ed', thBg:'#fb923c', thBorder:'#ea580c', rowHover:'#fed7aa', wrapBorder:'#ea580c', mfgHeader:'#fed7aa' },
  24: { bg:'linear-gradient(160deg,#fefce8,#fef08a,#fffbeb)',  tableBg:'#fefce8', thBg:'#fde047', thBorder:'#ca8a04', rowHover:'#fef9c3', wrapBorder:'#ca8a04', mfgHeader:'#fef9c3' },
  25: { bg:'linear-gradient(160deg,#faf5ff,#e9d5ff,#fef3c7)',  tableBg:'#faf5ff', thBg:'#d8b4fe', thBorder:'#9333ea', rowHover:'#e9d5ff', wrapBorder:'#9333ea', mfgHeader:'#e9d5ff' },
  26: { bg:'linear-gradient(160deg,#fdf2f8,#f5d0fe,#fdf2f8)',  tableBg:'#fdf2f8', thBg:'#e879f9', thBorder:'#a21caf', rowHover:'#f5d0fe', wrapBorder:'#a21caf', mfgHeader:'#f5d0fe' },
  27: { bg:'linear-gradient(160deg,#faf9f7,#e7e5e4,#fef3c7)',  tableBg:'#faf9f7', thBg:'#d6d3d1', thBorder:'#78716c', rowHover:'#e7e5e4', wrapBorder:'#78716c', mfgHeader:'#e7e5e4' },
  28: { bg:'linear-gradient(160deg,#f0fdf4,#bbf7d0,#f7fee7)',  tableBg:'#f0fdf4', thBg:'#86efac', thBorder:'#16a34a', rowHover:'#bbf7d0', wrapBorder:'#16a34a', mfgHeader:'#bbf7d0' },
  29: { bg:'linear-gradient(160deg,#fefce8,#d6d3d1,#fef9c3)',  tableBg:'#fefce8', thBg:'#d6d3d1', thBorder:'#78716c', rowHover:'#e7e5e4', wrapBorder:'#78716c', mfgHeader:'#e7e5e4' },
  30: { bg:'linear-gradient(160deg,#eff6ff,#bfdbfe,#e0f2fe)',  tableBg:'#eff6ff', thBg:'#7dd3fc', thBorder:'#0ea5e9', rowHover:'#bfdbfe', wrapBorder:'#0ea5e9', mfgHeader:'#bfdbfe' },
  31: { bg:'linear-gradient(160deg,#fefce8,#fde68a,#fef9c3)',  tableBg:'#fefce8', thBg:'#fcd34d', thBorder:'#d97706', rowHover:'#fde68a', wrapBorder:'#d97706', mfgHeader:'#fde68a' },
  32: { bg:'linear-gradient(160deg,#fefce8,#fef08a,#f0fdf4)',  tableBg:'#fefce8', thBg:'#fde047', thBorder:'#65a30d', rowHover:'#fef9c3', wrapBorder:'#65a30d', mfgHeader:'#fef9c3' },
  33: { bg:'linear-gradient(160deg,#f0fdf4,#bbf7d0,#fefce8)',  tableBg:'#f0fdf4', thBg:'#bbf7d0', thBorder:'#22c55e', rowHover:'#dcfce7', wrapBorder:'#22c55e', mfgHeader:'#dcfce7' },
};

const DEFAULT_THEME: CatTheme = {
  bg:'linear-gradient(160deg,#f8fafc,#f1f5f9)',
  tableBg:'#ffffff', thBg:'#f9fafb', thBorder:'#e5e7eb',
  rowHover:'#f9fafb', wrapBorder:'#e5e7eb', mfgHeader:'#f9fafb'
};

const CATEGORY_BG: Record<number, string> = {
  // ── ראשיות ──
  1:  'linear-gradient(160deg, #f0fdf4 0%, #dcfce7 50%, #f0fdf4 100%)',   // סלטים — ירוק
  2:  'linear-gradient(160deg, #fffbeb 0%, #fef3c7 50%, #fff7ed 100%)',   // רטבים — שמנת-חמאה
  3:  'linear-gradient(160deg, #eff6ff 0%, #dbeafe 50%, #eff6ff 100%)',   // דגים — כחול
  4:  'linear-gradient(160deg, #fefce8 0%, #fef9c3 50%, #fffbeb 100%)',   // בצקים — חיטה
  5:  'linear-gradient(160deg, #fefce8 0%, #fef08a 30%, #fefce8 100%)',   // ביצים — צהוב
  6:  'linear-gradient(160deg, #fff7ed 0%, #ffedd5 50%, #fef3c7 100%)',   // תבלינים — כתום-חמים
  7:  'linear-gradient(160deg, #fff7ed 0%, #fed7aa 40%, #fef3c7 100%)',   // מרקים — כתום עמוק
  // ── תתי-קטגוריות סלטים ──
  8:  'linear-gradient(160deg, #fefce8 0%, #fef9c3 50%, #fff7ed 100%)',   // חומוס — בז' חמים
  9:  'linear-gradient(160deg, #faf5ff 0%, #ede9fe 50%, #faf5ff 100%)',   // חצילים — סגול
  10: 'linear-gradient(160deg, #fff1f2 0%, #ffe4e6 50%, #fff7ed 100%)',   // חריפים — אדום-ורוד
  11: 'linear-gradient(160deg, #f7fee7 0%, #ecfccb 50%, #f0fdf4 100%)',   // חמוצים — ירוק-ליים
  12: 'linear-gradient(160deg, #fff7ed 0%, #ffedd5 40%, #fef9c3 100%)',   // גזרים — כתום
  13: 'linear-gradient(160deg, #fff1f2 0%, #fecdd3 40%, #fff1f2 100%)',   // עגבניות — אדום בהיר
  16: 'linear-gradient(160deg, #fafaf9 0%, #f5f5f4 50%, #fef9c3 100%)',   // טחינה — שמנת
  17: 'linear-gradient(160deg, #f5f3ff 0%, #ede9fe 50%, #f5f3ff 100%)',   // כרוב — לבנדר
  18: 'linear-gradient(160deg, #fff7ed 0%, #fed7aa 50%, #fffbeb 100%)',   // דלעת — כתום עמוק
  19: 'linear-gradient(160deg, #f0fdf4 0%, #dcfce7 30%, #fff1f2 100%)',   // פלפלים — ירוק-אדום
  20: 'linear-gradient(160deg, #f0fdf4 0%, #bbf7d0 50%, #f0fdf4 100%)',   // מלפפון — ירוק
  21: 'linear-gradient(160deg, #f7fee7 0%, #d9f99d 40%, #ecfccb 100%)',   // אבוקדו — ירוק זית
  22: 'linear-gradient(160deg, #f0fdf4 0%, #dcfce7 50%, #ecfccb 100%)',   // ארטישוק — ירוק
  23: 'linear-gradient(160deg, #fff7ed 0%, #fdba74 30%, #fef3c7 100%)',   // בטטה — כתום-אדמה
  24: 'linear-gradient(160deg, #fefce8 0%, #fef08a 40%, #fffbeb 100%)',   // ביצים — צהוב
  25: 'linear-gradient(160deg, #faf5ff 0%, #e9d5ff 40%, #fef3c7 100%)',   // עדשים — חום-סגול
  26: 'linear-gradient(160deg, #fdf2f8 0%, #f5d0fe 50%, #fdf2f8 100%)',   // סלק — ורוד-מגנטה
  27: 'linear-gradient(160deg, #faf9f7 0%, #e7e5e4 50%, #fef3c7 100%)',   // פטריות — חום בהיר
  28: 'linear-gradient(160deg, #f0fdf4 0%, #bbf7d0 40%, #f7fee7 100%)',   // קישואים — ירוק בהיר
  29: 'linear-gradient(160deg, #fefce8 0%, #d6d3d1 30%, #fef9c3 100%)',   // תפו"א — אדמה-שמנת
  30: 'linear-gradient(160deg, #eff6ff 0%, #bfdbfe 40%, #e0f2fe 100%)',   // טונה — כחול ים
  31: 'linear-gradient(160deg, #fefce8 0%, #fde68a 40%, #fef9c3 100%)',   // תירס — צהוב
  32: 'linear-gradient(160deg, #fefce8 0%, #fef08a 50%, #f0fdf4 100%)',   // לימון — צהוב-ירוק
  33: 'linear-gradient(160deg, #f0fdf4 0%, #bbf7d0 40%, #fefce8 100%)',   // טבולה — ירוק-שמנת
};

const DEFAULT_BG = 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)';

type SortField = 'nameHebrew' | 'weight' | 'manufacturer' | 'kashrut' | 'price';
type SortDir = 'asc' | 'desc';
type ViewMode = 'table' | 'byManufacturer';

function sortProducts(list: ProductDto[], field: SortField, dir: SortDir): ProductDto[] {
  return [...list].sort((a, b) => {
    let cmp = 0;
    if (field === 'weight') {
      cmp = a.weight - b.weight;
    } else if (field === 'price') {
      cmp = (a.price ?? -1) - (b.price ?? -1);
    } else {
      const av = (a[field] ?? '').toString().toLowerCase();
      const bv = (b[field] ?? '').toString().toLowerCase();
      cmp = av.localeCompare(bv, 'he');
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

function formatWeight(weight: number): string {
  if (weight < 1) return `${Math.round(weight * 1000)} גר'`;
  return `${weight} ק״ג`;
}

function groupByManufacturer(list: ProductDto[]): Map<string, ProductDto[]> {
  const map = new Map<string, ProductDto[]>();
  for (const p of list) {
    const key = p.manufacturer || 'לא ידוע';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  const sorted = Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, 'he-IL'));
  const result = new Map<string, ProductDto[]>();
  sorted.forEach(([k, v]) => result.set(k, v));
  return result;
}

export function ProductListPage(): JSX.Element {
  const { categoryId: categoryIdStr } = useParams<{ categoryId: string }>();
  const categoryId = parseInt(categoryIdStr ?? '0', 10);
  const [searchParams] = useSearchParams();
  const department = searchParams.get('department') ?? undefined;

  const parentIdStr = searchParams.get('parentId');
  const parentId    = parentIdStr ? parseInt(parentIdStr, 10) : null;

  const { data: products, isLoading, isError, error } = useProducts(categoryId, department);
  const { data: category } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => getCategoryById(categoryId),
    enabled: categoryId > 0,
  });

  const { isAdmin } = useAuth();
  const isGuest = !isAdmin;
  const { cartItems, addToCart, removeFromCart, updateQuantity } = useCart();

  // Source of truth for guest checkboxes = cart IDs (persists across navigation)
  const selectedIds = new Set(cartItems.map((ci) => ci.product.id));
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('nameHebrew');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [collapsedManufacturers, setCollapsedManufacturers] = useState<Set<string>>(new Set());

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<ProductDto | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<ProductDto | null>(null);
  const [zoomImage, setZoomImage] = useState<{ url: string; name: string } | null>(null);

  function handleSort(field: SortField): void {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function sortIndicator(field: SortField): string {
    if (field !== sortField) return ' ⇅';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  function toggleManufacturer(name: string): void {
    setCollapsedManufacturers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products ?? [];
    return (products ?? []).filter((p) =>
      p.nameHebrew?.toLowerCase().includes(q) ||
      p.manufacturer?.toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  const sortedProducts = useMemo(
    () => sortProducts(filteredProducts, sortField, sortDir),
    [filteredProducts, sortField, sortDir]
  );

  const grouped = useMemo(
    () => groupByManufacturer(sortedProducts),
    [sortedProducts]
  );

  function handleAddProduct(): void { setEditProduct(null); setIsFormOpen(true); }
  function handleEditProduct(p: ProductDto): void { setEditProduct(p); setIsFormOpen(true); }
  function handleDeleteProduct(p: ProductDto): void { setDeleteProduct(p); }
  function handleFormClose(): void { setIsFormOpen(false); setEditProduct(null); }
  function handleDeleteClose(): void { setDeleteProduct(null); }

  function toggleSelect(product: ProductDto): void {
    if (selectedIds.has(product.id)) {
      removeFromCart(product.id);
    } else {
      addToCart([product]);
    }
  }

  function toggleSelectAll(list: ProductDto[]): void {
    const allSelected = list.every((p) => selectedIds.has(p.id));
    if (allSelected) {
      list.forEach((p) => removeFromCart(p.id));
    } else {
      addToCart(list.filter((p) => !selectedIds.has(p.id)));
    }
  }

  const earlyCatClass = CATEGORY_THEMES[categoryId] ? `cat-bg-${categoryId}` : 'cat-bg-default';

  if (isLoading) {
    return (
      <div className={`product-list-page ${earlyCatClass}`} style={{ minHeight: '100vh' }}>
        <div className="loading-state"><div className="spinner" /><p>טוען מוצרים...</p></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`product-list-page ${earlyCatClass}`} style={{ minHeight: '100vh' }}>
        <div className="error-state">
          <p>שגיאה בטעינת המוצרים.</p>
          <p>{error instanceof Error ? error.message : 'שגיאה לא ידועה'}</p>
        </div>
      </div>
    );
  }

  function theadRow(list: ProductDto[]) {
    const allSelected = list.length > 0 && list.every((p) => selectedIds.has(p.id));
    const someSelected = list.some((p) => selectedIds.has(p.id));
    return (
      <tr>
        {isGuest && (
          <th className="th-checkbox">
            <input
              type="checkbox"
              className="product-checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
              onChange={() => toggleSelectAll(list)}
              aria-label="בחר הכל"
            />
          </th>
        )}
        {isGuest && <th className="th-qty">כמות</th>}
        <th>תמונה</th>
        <th>מחלקה</th>
        <th className="th-sortable" onClick={() => handleSort('nameHebrew')}
            aria-sort={sortField === 'nameHebrew' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
          שם מוצר{sortIndicator('nameHebrew')}
        </th>
        <th className="th-sortable" onClick={() => handleSort('weight')}
            aria-sort={sortField === 'weight' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
          משקל{sortIndicator('weight')}
        </th>
        <th className="th-sortable" onClick={() => handleSort('manufacturer')}
            aria-sort={sortField === 'manufacturer' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
          יצרן{sortIndicator('manufacturer')}
        </th>
        <th className="th-sortable" onClick={() => handleSort('kashrut')}
            aria-sort={sortField === 'kashrut' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
          כשרות{sortIndicator('kashrut')}
        </th>
        {isAdmin && (
          <th className="th-sortable" onClick={() => handleSort('price')}
              aria-sort={sortField === 'price' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            מחיר{sortIndicator('price')}
          </th>
        )}
        {isAdmin && <th>פעולות</th>}
      </tr>
    );
  }

  function productRow(product: ProductDto) {
    const isChecked = selectedIds.has(product.id);
    const cartItem  = cartItems.find((ci) => ci.product.id === product.id);
    const qty       = cartItem?.quantity ?? 1;
    return (
      <tr
        key={product.id}
        className={isGuest && isChecked ? 'product-row--selected' : undefined}
        onClick={isGuest ? () => toggleSelect(product) : undefined}
        style={isGuest ? { cursor: 'pointer' } : undefined}
      >
        {isGuest && (
          <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              className="product-checkbox"
              checked={isChecked}
              onChange={() => toggleSelect(product)}
              aria-label={`בחר ${product.nameHebrew}`}
            />
          </td>
        )}
        {isGuest && (
          <td className="td-qty" onClick={(e) => e.stopPropagation()}>
            {isChecked && (
              <div className="qty-stepper">
                <button
                  className="qty-btn qty-btn--minus"
                  onClick={() => updateQuantity(product.id, Math.max(1, qty - 1))}
                  disabled={qty <= 1}
                  aria-label="הפחת כמות"
                >−</button>
                <span className="qty-value">{qty}</span>
                <button
                  className="qty-btn qty-btn--plus"
                  onClick={() => updateQuantity(product.id, qty + 1)}
                  aria-label="הוסף כמות"
                >+</button>
              </div>
            )}
          </td>
        )}
        <td>
          {product.imageUrl ? (
            <img
              src={productImageUrl(product.imageUrl)!}
              alt={product.nameHebrew}
              className="product-thumbnail product-thumbnail--zoomable"
              onMouseEnter={() => setZoomImage({ url: productImageUrl(product.imageUrl)!, name: product.nameHebrew })}
              onMouseLeave={() => setZoomImage(null)}
              onFocus={() => setZoomImage({ url: productImageUrl(product.imageUrl)!, name: product.nameHebrew })}
              onBlur={() => setZoomImage(null)}
              tabIndex={0}
            />
          ) : (
            <div
              className="product-thumbnail-placeholder product-thumbnail-placeholder--zoomable"
              onMouseEnter={() => setZoomImage({ url: '/logo.png', name: 'התמונה תתוסף בקרוב' })}
              onMouseLeave={() => setZoomImage(null)}
              onFocus={() => setZoomImage({ url: '/logo.png', name: 'התמונה תתוסף בקרוב' })}
              onBlur={() => setZoomImage(null)}
              tabIndex={0}
            >
              <img src="/logo.png" alt="" className="product-thumbnail-logo" />
              <span className="product-thumbnail-soon">התמונה תתוסף בקרוב</span>
            </div>
          )}
        </td>
        <td><DepartmentBadge department={product.department} /></td>
        <td>
          <p className="product-name-he">{product.nameHebrew}</p>
          {product.nameEnglish && <p className="product-name-en">{product.nameEnglish}</p>}
        </td>
        <td>{formatWeight(product.weight)}</td>
        <td>{product.manufacturer}</td>
        <td><KashrutBadge kashrut={product.kashrut} /></td>
        {isAdmin && <td>{product.price != null ? `₪${product.price.toFixed(2)}` : '—'}</td>}
        {isAdmin && (
          <td className="td-actions">
            <button className="action-btn" onClick={() => handleEditProduct(product)}
              title="ערוך מוצר" aria-label={`ערוך ${product.nameHebrew}`}>✏️</button>
            <button className="action-btn action-btn--delete" onClick={() => handleDeleteProduct(product)}
              title="מחק מוצר" aria-label={`מחק ${product.nameHebrew}`}>🗑️</button>
          </td>
        )}
      </tr>
    );
  }

  const theme = CATEGORY_THEMES[categoryId] ?? DEFAULT_THEME;
  const catClass = CATEGORY_THEMES[categoryId] ? `cat-bg-${categoryId}` : 'cat-bg-default';
  const themeVars = {
    minHeight: '100vh',
    '--cat-table-bg':    theme.tableBg,
    '--cat-th-bg':       theme.thBg,
    '--cat-th-border':   theme.thBorder,
    '--cat-row-hover':   theme.rowHover,
    '--cat-wrap-border': theme.wrapBorder,
    '--cat-mfg-header':  theme.mfgHeader,
  } as React.CSSProperties;

  return (
    <div className={`product-list-page ${catClass}`} style={themeVars}>
      <div className="product-list-header">
        <div className="product-list-header-right">
          <div>
            <h1 className="product-list-title">
              {category?.nameHe ?? `קטגוריה ${categoryId}`}
            </h1>
            <p className="product-list-subtitle">
              {sortedProducts.length} {sortedProducts.length === 1 ? 'מוצר' : 'מוצרים'}
            </p>
          </div>
          <Link
            to={parentId ? `/categories?expand=${parentId}` : '/categories'}
            className="back-link"
          >
            חזרה לקטגוריות ←
          </Link>
        </div>
        {isAdmin && (
          <button className="add-product-btn" onClick={handleAddProduct}>הוסף מוצר +</button>
        )}
      </div>

      {/* Search bar */}
      <div className="product-search-bar" dir="rtl">
        <input
          type="search"
          className="product-search-input"
          placeholder="חיפוש לפי שם מוצר או יצרן..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="חיפוש מוצרים"
        />
        {searchQuery && (
          <span className="product-search-count">
            {sortedProducts.length} תוצאות
          </span>
        )}
      </div>

      {/* View mode toggle */}
      <div className="view-mode-toggle" dir="rtl">
        <button
          className={`view-btn${viewMode === 'table' ? ' view-btn--active' : ''}`}
          onClick={() => setViewMode('table')}
        >
          טבלה
        </button>
        <button
          className={`view-btn${viewMode === 'byManufacturer' ? ' view-btn--active' : ''}`}
          onClick={() => setViewMode('byManufacturer')}
        >
          לפי יצרן
        </button>
      </div>

      {sortedProducts.length === 0 ? (
        <div className="empty-state">אין מוצרים בקטגוריה זו</div>
      ) : viewMode === 'table' ? (
        <div className="product-table-wrapper">
          <table className="product-table">
            <thead>{theadRow(sortedProducts)}</thead>
            <tbody>{sortedProducts.map(productRow)}</tbody>
          </table>
        </div>
      ) : (
        <div className="manufacturer-groups">
          {Array.from(grouped.entries()).map(([mfr, items]) => {
            const isCollapsed = collapsedManufacturers.has(mfr);
            return (
              <div key={mfr} className="manufacturer-group">
                <button
                  className="manufacturer-header"
                  onClick={() => toggleManufacturer(mfr)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="manufacturer-name">{mfr}</span>
                  <span className="manufacturer-count">{items.length} {items.length === 1 ? 'מוצר' : 'מוצרים'}</span>
                  <span className={`manufacturer-chevron${isCollapsed ? '' : ' manufacturer-chevron--open'}`}>›</span>
                </button>
                {!isCollapsed && (
                  <div className="product-table-wrapper manufacturer-table">
                    <table className="product-table">
                      <thead>{theadRow(items)}</thead>
                      <tbody>{items.map(productRow)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selection bar — guest only */}
      {isGuest && selectedIds.size > 0 && (
        <div className="selection-bar" dir="rtl">
          <span className="selection-bar__count">
            🛒 {selectedIds.size} {selectedIds.size === 1 ? 'מוצר' : 'מוצרים'} בסל
            {cartItems.reduce((s, ci) => s + ci.quantity, 0) > selectedIds.size && (
              <span className="selection-bar__units"> · {cartItems.reduce((s, ci) => s + ci.quantity, 0)} יח'</span>
            )}
          </span>
          <button
            className="selection-bar__clear"
            onClick={() => {
              // remove only the products visible in this page from the cart
              sortedProducts.forEach((p) => removeFromCart(p.id));
            }}
          >
            נקה בחירה
          </button>
        </div>
      )}

      {/* Image zoom overlay */}
      {zoomImage && (
        <div className="img-zoom-overlay" aria-hidden="true">
          <img src={zoomImage.url} alt={zoomImage.name} className="img-zoom-img" />
          <p className="img-zoom-caption">{zoomImage.name}</p>
        </div>
      )}

      <ProductFormModal isOpen={isFormOpen} onClose={handleFormClose} product={editProduct} categoryId={categoryId} />
      <DeleteProductModal isOpen={deleteProduct !== null} onClose={handleDeleteClose} product={deleteProduct} />
    </div>
  );
}
